export const RESERVED_SUBDOMAINS = [
  "www",
  "app",
  "api",
  "admin",
  "auth",
  "mail",
  "static",
  "assets",
  "support",
  "cdn",
] as const;

const reserved = new Set<string>(RESERVED_SUBDOMAINS);
const tenantSlugPattern = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

export const domainConfig = {
  productionBaseDomain: import.meta.env.VITE_BASE_DOMAIN?.trim() || "positronico.com",
  localBaseDomain: import.meta.env.VITE_LOCAL_BASE_DOMAIN?.trim() || "positronico.localhost",
  publicUrl: import.meta.env.VITE_PUBLIC_URL?.trim() || "https://positronico.com",
  appUrl: import.meta.env.VITE_APP_URL?.trim() || "https://app.positronico.com",
  apiUrl: import.meta.env.VITE_API_URL?.trim() || "https://api.positronico.com",
  stagingOrigin: import.meta.env.VITE_STAGING_ORIGIN?.trim().replace(/\/$/, "") || "",
} as const;

export type RuntimeLocation = Pick<Location, "hostname" | "port"> & Partial<Pick<Location, "origin" | "pathname" | "protocol">>;

export type Surface =
  | { kind: "public" }
  | { kind: "app" }
  | { kind: "api" }
  | { kind: "tenant"; slug: string }
  | { kind: "reserved"; subdomain: string }
  | { kind: "unknown" };

function normalizedHostname(hostname: string) {
  return hostname.trim().toLowerCase().replace(/\.$/, "");
}

export function isValidTenantSlug(slug: string) {
  return tenantSlugPattern.test(slug) && !reserved.has(slug);
}

function classifyWithinBase(hostname: string, baseDomain: string): Surface | null {
  if (hostname === baseDomain) return { kind: "public" };
  if (!hostname.endsWith(`.${baseDomain}`)) return null;
  const subdomain = hostname.slice(0, -(baseDomain.length + 1));
  if (subdomain.includes(".")) return { kind: "unknown" };
  if (subdomain === "app") return { kind: "app" };
  if (subdomain === "api") return { kind: "api" };
  if (reserved.has(subdomain)) return { kind: "reserved", subdomain };
  return isValidTenantSlug(subdomain) ? { kind: "tenant", slug: subdomain } : { kind: "unknown" };
}

export function classifyHostname(hostname: string, defaultTenant = import.meta.env.VITE_DEFAULT_TENANT?.trim() || "dlr"): Surface {
  const normalized = normalizedHostname(hostname);
  const local = classifyWithinBase(normalized, domainConfig.localBaseDomain);
  if (local) return local;
  const production = classifyWithinBase(normalized, domainConfig.productionBaseDomain);
  if (production) return production;
  if ((normalized === "localhost" || normalized === "127.0.0.1" || normalized === "::1") && isValidTenantSlug(defaultTenant)) {
    return { kind: "tenant", slug: defaultTenant };
  }
  return { kind: "unknown" };
}

function normalizedOrigin(location: RuntimeLocation) {
  if (location.origin && location.origin !== "null") return location.origin.replace(/\/$/, "");
  const protocol = location.protocol || (isLocalDomain(location.hostname) ? "http:" : "https:");
  return `${protocol}//${location.hostname}${location.port ? `:${location.port}` : ""}`;
}

function stagingSurface(pathname: string): Surface {
  const parts = pathname.split("/").filter(Boolean);
  if (parts[0] === "app") return { kind: "app" };
  if (parts[0] === "tenant" && parts[1] && isValidTenantSlug(parts[1])) return { kind: "tenant", slug: parts[1] };
  if (parts[0] === "__api") return { kind: "api" };
  return { kind: "public" };
}

export function isStagingLocation(location: RuntimeLocation = window.location, stagingOrigin = domainConfig.stagingOrigin) {
  return Boolean(stagingOrigin && normalizedOrigin(location) === stagingOrigin.replace(/\/$/, ""));
}

export function classifyLocation(location: RuntimeLocation = window.location, defaultTenant = import.meta.env.VITE_DEFAULT_TENANT?.trim() || "dlr", stagingOrigin = domainConfig.stagingOrigin): Surface {
  if (isStagingLocation(location, stagingOrigin)) return stagingSurface(location.pathname || "/");
  return classifyHostname(location.hostname, defaultTenant);
}

export function routerBasePath(location: RuntimeLocation = window.location, stagingOrigin = domainConfig.stagingOrigin) {
  if (!isStagingLocation(location, stagingOrigin)) return "/";
  const surface = classifyLocation(location, undefined, stagingOrigin);
  if (surface.kind === "app") return "/app";
  if (surface.kind === "tenant") return `/tenant/${surface.slug}`;
  return "/";
}

export function isLocalDomain(hostname: string) {
  const normalized = normalizedHostname(hostname);
  return normalized === "localhost" || normalized === "127.0.0.1" || normalized === "::1" || normalized === domainConfig.localBaseDomain || normalized.endsWith(`.${domainConfig.localBaseDomain}`);
}

export function runtimeUrls(location: RuntimeLocation = window.location, stagingOrigin = domainConfig.stagingOrigin) {
  if (isStagingLocation(location, stagingOrigin)) {
    const origin = stagingOrigin.replace(/\/$/, "");
    return {
      ...domainConfig,
      publicUrl: origin,
      appUrl: `${origin}/app`,
      apiUrl: import.meta.env.VITE_API_URL?.trim() || `${origin}/__api`,
    };
  }
  if (!isLocalDomain(location.hostname)) return domainConfig;
  const suffix = location.port ? `:${location.port}` : "";
  return {
    ...domainConfig,
    publicUrl: `http://${domainConfig.localBaseDomain}${suffix}`,
    appUrl: `http://app.${domainConfig.localBaseDomain}${suffix}`,
    apiUrl: import.meta.env.VITE_API_URL?.trim() || `http://api.${domainConfig.localBaseDomain}:8000`,
  };
}

export function joinUrlPath(baseUrl: string, route: string) {
  const result = new URL(baseUrl);
  const requested = new URL(route.startsWith("/") ? route : `/${route}`, "https://route.invalid");
  const prefix = result.pathname.replace(/\/$/, "");
  result.pathname = `${prefix}${requested.pathname}` || "/";
  result.search = requested.search;
  result.hash = requested.hash;
  return result.toString();
}

export function tenantUrl(slug: string, path = "/", location: RuntimeLocation = window.location, stagingOrigin = domainConfig.stagingOrigin) {
  if (!isValidTenantSlug(slug)) throw new Error("Tenant slug inválido.");
  if (isStagingLocation(location, stagingOrigin)) return joinUrlPath(`${stagingOrigin.replace(/\/$/, "")}/tenant/${slug}`, path);
  const baseDomain = isLocalDomain(location.hostname) ? domainConfig.localBaseDomain : domainConfig.productionBaseDomain;
  const protocol = isLocalDomain(location.hostname) ? "http" : "https";
  const port = isLocalDomain(location.hostname) && location.port ? `:${location.port}` : "";
  return new URL(path, `${protocol}://${slug}.${baseDomain}${port}`).toString();
}

export function validateReturnTo(value: string | null | undefined, location: RuntimeLocation = window.location, stagingOrigin = domainConfig.stagingOrigin) {
  const urls = runtimeUrls(location, stagingOrigin);
  if (!value || value.length > 2048 || value.includes("\\")) return urls.appUrl;
  try {
    const staging = isStagingLocation(location, stagingOrigin);
    const target = staging && value.startsWith("/") ? new URL(joinUrlPath(urls.appUrl, value)) : new URL(value, `${urls.appUrl.replace(/\/$/, "")}/`);
    if (target.username || target.password) return urls.appUrl;
    const surface = classifyLocation(target, undefined, stagingOrigin);
    if (staging && target.origin !== stagingOrigin.replace(/\/$/, "")) return urls.appUrl;
    const local = isLocalDomain(target.hostname);
    if (!staging && local !== isLocalDomain(location.hostname)) return urls.appUrl;
    if (!staging && (local ? target.protocol !== "http:" && target.protocol !== "https:" : target.protocol !== "https:")) return urls.appUrl;
    if (surface.kind !== "app" && surface.kind !== "tenant") return urls.appUrl;
    const relativePath = staging && surface.kind === "app" ? target.pathname.slice("/app".length) || "/" : staging && surface.kind === "tenant" ? target.pathname.slice(`/tenant/${surface.slug}`.length) || "/" : target.pathname;
    if (surface.kind === "app" && ["/login", "/forgot-password", "/set-password", "/reset-password", "/auth/callback"].some((path) => relativePath === path || relativePath.startsWith(`${path}/`))) return urls.appUrl;
    if (surface.kind === "tenant" && !["/", "/proyecto", "/archivos", "/cotizaciones", "/agentes"].some((path) => relativePath === path || (path !== "/" && relativePath.startsWith(`${path}/`)))) return urls.appUrl;
    return target.toString();
  } catch {
    return urls.appUrl;
  }
}

export function surfaceUrl(path: string, location: RuntimeLocation = window.location) {
  const surface = classifyLocation(location);
  const urls = runtimeUrls(location);
  if (surface.kind === "app") return joinUrlPath(urls.appUrl, path);
  if (surface.kind === "tenant") return tenantUrl(surface.slug, path, location);
  return joinUrlPath(urls.publicUrl, path);
}
